import { useQuery } from '@tanstack/react-query';

import { fetchCurrentUser } from '../api/currentUser';
import type { CurrentUser } from '../api/currentUser';

/** The cache key, exported so anything signing out can drop exactly this. */
export const currentUserKey = ['currentUser'] as const;

export function useCurrentUser({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CurrentUser>({
    queryKey: currentUserKey,
    queryFn: fetchCurrentUser,

    // Off when there is no token: asking who is signed in while signed out is a
    // guaranteed 401, which the interceptor would try to refresh and then report
    // as a session ending -- for somebody who never had one.
    enabled,

    // Who is signed in does not change while the tab is open, and a token that
    // stopped working produces a 401 the next real request surfaces anyway.

    /*
     * NOT RETRIED, and the reason is what waits on it.
     *
     * Every route guard reads this query's outcome, so each retry is another second of
     * loader in front of a person who cannot be shown anything until it settles. The
     * default three with backoff is three or four seconds of nothing.
     *
     * <p>A 401 has already been through the refresh interceptor by the time it arrives
     * here, so there is nothing left to recover -- asking again asks a settled question. A
     * 500 or an unreachable server is not the session's fault and is not treated as one:
     * the token stays in storage, the screen says the account could not be loaded, and a
     * reload is the retry. That is one honest answer in a second rather than a loader that
     * might still come good.
     */
    retry: false,
  });
}
